import type { InfiniteData, QueryClient, QueryKey } from "@tanstack/react-query"
import { notificationQueryKeys } from "../api/notificationApi"
import type {
  NotificationDTO,
  NotificationsPage,
  UnreadCountResponse,
} from "../types"

export type NotificationsInfiniteData = InfiniteData<
  NotificationsPage,
  string | undefined
>

export type ListsSnapshot = [QueryKey, NotificationsInfiniteData | undefined][]

export interface NotificationMutationContext {
  previousCount?: UnreadCountResponse
  previousLists?: ListsSnapshot
}

export const cancelNotificationQueries = (queryClient: QueryClient) =>
  Promise.all([
    queryClient.cancelQueries({ queryKey: notificationQueryKeys.unreadCount }),
    queryClient.cancelQueries({ queryKey: notificationQueryKeys.lists }),
  ])

export const snapshotNotificationCaches = (
  queryClient: QueryClient,
): NotificationMutationContext => ({
  previousCount: queryClient.getQueryData<UnreadCountResponse>(
    notificationQueryKeys.unreadCount,
  ),
  previousLists: queryClient.getQueriesData<NotificationsInfiniteData>({
    queryKey: notificationQueryKeys.lists,
  }),
})

/** Applies `patch` to every cached list variant (all / unread / needs-action). */
export const patchAllLists = (
  queryClient: QueryClient,
  patch: (item: NotificationDTO) => NotificationDTO,
) => {
  queryClient.setQueriesData<NotificationsInfiniteData>(
    { queryKey: notificationQueryKeys.lists },
    (data) =>
      data
        ? {
            ...data,
            pages: data.pages.map((page) => ({
              ...page,
              items: page.items.map(patch),
            })),
          }
        : data,
  )
}

export const findCachedNotification = (
  queryClient: QueryClient,
  notificationId: string,
): NotificationDTO | undefined => {
  const lists = queryClient.getQueriesData<NotificationsInfiniteData>({
    queryKey: notificationQueryKeys.lists,
  })
  for (const [, data] of lists) {
    for (const page of data?.pages ?? []) {
      const match = page.items.find((item) => item.id === notificationId)
      if (match) {
        return match
      }
    }
  }
  return undefined
}

export const setUnreadCount = (
  queryClient: QueryClient,
  update: (previous: UnreadCountResponse) => UnreadCountResponse,
) => {
  const previous = queryClient.getQueryData<UnreadCountResponse>(
    notificationQueryKeys.unreadCount,
  )
  if (previous) {
    queryClient.setQueryData<UnreadCountResponse>(
      notificationQueryKeys.unreadCount,
      update(previous),
    )
  }
}

export const rollbackNotificationCaches = (
  queryClient: QueryClient,
  context: NotificationMutationContext | undefined,
) => {
  if (context?.previousCount) {
    queryClient.setQueryData(
      notificationQueryKeys.unreadCount,
      context.previousCount,
    )
  }
  for (const [queryKey, data] of context?.previousLists ?? []) {
    queryClient.setQueryData(queryKey, data)
  }
}

export const invalidateNotificationCaches = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({
    queryKey: notificationQueryKeys.unreadCount,
  })
  queryClient.invalidateQueries({ queryKey: notificationQueryKeys.lists })
}
